import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { Permission } from '~/acl.ts';
import { identify } from '~/analytics';
import { setAccessToken } from '~/api';
import { refresh } from '~/auth/api.ts';
import { clearToken, getRefreshToken, getToken, shouldRefresh, storeToken } from '~/auth/token';
import { AuthenticatedUser, AuthUser, getAuthenticatedUser, newUser } from '~/auth/user';
import { setupMonitoringUser } from '~/monitoring.ts';
import storage from '~/utils/storage.ts';

export const AuthContext = createContext<Auth | null>(null);

export function useAuthData(): Auth {
    const data = useContext(AuthContext);
    return data || ({} as Auth);
}

export type Auth = {
    user: AuthUser;
    loading: boolean;
    authenticated: boolean;
    authenticate: (token: string, expiration: number, refresh?: string) => Promise<void>;
    refresh: () => void;
};

type AuthOptions = {
    silent?: boolean;
    force?: boolean;
    authenticated?: boolean;
};

export function useAuth(): Auth {
    const search = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    // Manage page uses the `code` query param to provide access. This conflicts with
    // the site-wide auth code recognition, so we must make sure to treat it as if we
    // did not have any code for this page.
    const code = path.startsWith('/manage/') ? '' : search.get('code');
    const [loading, setLoading] = useState(false);
    const [user, _setUser] = useState(newUser(null));
    const token = getToken();
    const [refreshes, setRefreshes] = useState(0);
    const isAuthPage = path === '/auth';

    const setUser = (user: AuthenticatedUser | null) => {
        _setUser(newUser(user));
    };

    const auth = async (token: string, opts: AuthOptions = {}) => {
        const { silent = false, force = false, authenticated = false } = opts;
        if ((loading || user.authenticated) && !force) return;
        if ((token || code) && !silent) setLoading(true);

        if (!token && code && !isAuthPage) {
            const returnTo = path + searchWithoutCode(search);
            window.location.href = `/auth?code=${code}&return_to=${encodeURIComponent(returnTo)}`;
            return;
        }

        try {
            if (token) {
                setAccessToken(token);

                if (shouldRefresh()) {
                    const newToken = await refresh(getRefreshToken(true));
                    storeToken(
                        newToken.token,
                        newToken.expires_at,
                        newToken.refresh_token || undefined,
                    );
                    setAccessToken(newToken.token);
                }

                const user = await getAuthenticatedUser();
                setUser(user);
                setupMonitoringUser(user);
                identify(newUser(user), authenticated);
            }
        } catch {
            setUser(null);
            clearToken();
        } finally {
            setLoading(false);
        }
    };

    // This function is what will be used to authenticate after the initial load.
    const authenticate = (token: string, expiration: number, refresh?: string): Promise<void> => {
        storeToken(token, expiration, refresh);
        return auth(token, { authenticated: true });
    };

    // This runs the initial authentication when the page is loaded.
    useEffect(() => {
        void auth(token!);
    }, []);

    // Whenever refresh is called, we run auth again silently.
    useEffect(() => {
        if (refreshes === 0) return; // ignore the first time
        void auth(token!, { silent: true, force: true });
    }, [refreshes]);

    return {
        user,
        loading: loading || Boolean(token && !user.authenticated),
        authenticated: user.authenticated,
        authenticate,
        refresh: () => setRefreshes((v) => v + 1),
    };
}

interface Rule {
    redirectTo?: string;
    is(user: AuthUser): boolean;
}

type RestrictedProps = {
    rules: Rule[];
    children: ReactNode;
};

export function Restricted({ rules, children }: RestrictedProps) {
    const { user } = useAuthData();
    for (const r of rules) {
        if (r.is(user)) {
            storage.setItem('return_to', window.location.pathname);
            return <Navigate to={r.redirectTo ?? '/'} />;
        }
    }

    return children;
}

type Guard = (user: AuthUser) => boolean;

type ProtectedRouteProps = {
    guard?: Guard;
    permissions?: Permission[];
    redirectUrl?: string;
    children: ReactNode;
};

export function ProtectedRoute({
    guard,
    permissions = [],
    redirectUrl = '/',
    children,
}: ProtectedRouteProps) {
    const { user } = useAuthData();
    if (guard && !guard(user)) {
        return <Navigate to={redirectUrl} />;
    }

    if (permissions.length > 0 && !user?.hasAccess(...permissions)) {
        return <Navigate to={redirectUrl} />;
    }

    return children;
}

export type ProtectedRouteArgs = (Guard | Permission)[];

/**
 * Protect a route passed to the React Router route object.
 * @param children JSX.Element
 * @param args Array<Function | Number> Args can contain 1 single function argument or a list of permissions.
 * @returns {JSX.Element}
 */
export function protectedRoute(children: ReactNode, ...args: ProtectedRouteArgs) {
    if (args.length === 1 && typeof args[0] === 'function') {
        return <ProtectedRoute guard={args[0]}>{children}</ProtectedRoute>;
    }
    return <ProtectedRoute permissions={args as Permission[]}>{children}</ProtectedRoute>;
}

function searchWithoutCode(search: URLSearchParams): string {
    if (search.size <= 1) return '';
    const entries = [];
    for (const [k, v] of search.entries()) {
        if (k !== 'code') {
            entries.push(`${k}=${v}`);
        }
    }
    return `?${entries.join('&')}`;
}
